import { SANTIM_PER_ETB, type Language } from './constants';
import { SharedError } from './errors';

/**
 * Money helpers. Everything is an integer number of santim (1 ETB = 100 santim).
 * Never use floating point for money. Multiplications that can exceed 2^53 use BigInt.
 */
export type Santim = number;

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);

export function isSantim(value: unknown): value is Santim {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

export function assertSantim(value: unknown, label = 'amount'): asserts value is Santim {
  if (!isSantim(value)) {
    throw new SharedError('INVALID_MONEY', `${label} must be a non-negative safe integer (santim)`);
  }
}

/** Convert a BigInt back to a safe number or throw. */
export function toSafeNumber(value: bigint, label = 'value'): number {
  if (value > MAX_SAFE || value < -MAX_SAFE) {
    throw new SharedError('INVALID_MONEY', `${label} exceeds the safe integer range`);
  }
  return Number(value);
}

/**
 * floor(a * b / c) computed exactly with BigInt. `a` and `b` must be non-negative safe
 * integers and `c` a positive safe integer.
 */
export function mulDivFloor(a: number, b: number, c: number): number {
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b) || !Number.isSafeInteger(c)) {
    throw new SharedError('INVALID_MONEY', 'mulDivFloor arguments must be safe integers');
  }
  if (a < 0 || b < 0) {
    throw new SharedError('INVALID_MONEY', 'mulDivFloor does not accept negative operands');
  }
  if (c <= 0) {
    throw new SharedError('INVALID_MONEY', 'mulDivFloor divisor must be positive');
  }
  return toSafeNumber((BigInt(a) * BigInt(b)) / BigInt(c), 'mulDivFloor result');
}

/** floor(amount * bps / 10000). */
export function bpsOf(amount: Santim, bps: number): Santim {
  if (!Number.isInteger(bps) || bps < 0 || bps > 10_000) {
    throw new SharedError('INVALID_MONEY', 'bps must be an integer between 0 and 10000');
  }
  return mulDivFloor(amount, bps, 10_000);
}

const ETB_INPUT = /^(\d+)(?:\.(\d{1,2}))?$/;

/**
 * Parse a user-entered ETB amount ("12", "12.5", "12.50") into santim.
 * Rejects negatives, more than two decimals, exponents, separators and unsafe sizes.
 */
export function etbToSantim(input: string | number): Santim {
  const raw = typeof input === 'number' ? numberToPlainString(input) : input.trim();
  const match = ETB_INPUT.exec(raw);
  if (!match) {
    throw new SharedError('INVALID_MONEY', `Invalid ETB amount: "${String(input)}"`);
  }
  const whole = BigInt(match[1] as string);
  const fraction = BigInt((match[2] ?? '').padEnd(2, '0') || '0');
  const total = whole * BigInt(SANTIM_PER_ETB) + fraction;
  return toSafeNumber(total, 'amount');
}

function numberToPlainString(n: number): string {
  if (!Number.isFinite(n)) {
    throw new SharedError('INVALID_MONEY', 'Amount must be a finite number');
  }
  // Exponent notation (e.g. 1e21) or negatives fail the ETB_INPUT regex and are rejected.
  return String(n);
}

/** 1250 -> "12.50". Negative values keep their sign ("-12.50"). No thousands separators. */
export function santimToEtb(santim: Santim): string {
  if (!Number.isSafeInteger(santim)) {
    throw new SharedError('INVALID_MONEY', 'santim must be a safe integer');
  }
  const negative = santim < 0;
  const abs = BigInt(Math.abs(santim));
  const whole = abs / BigInt(SANTIM_PER_ETB);
  const fraction = (abs % BigInt(SANTIM_PER_ETB)).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

export interface FormatEtbOptions {
  language?: Language;
  /** Omit the currency label. */
  bare?: boolean;
}

/**
 * 123450 -> "ETB 1,234.50" (en) or "ብር 1,234.50" (am). Digits are always Latin.
 */
export function formatEtb(santim: Santim, options: FormatEtbOptions = {}): string {
  const plain = santimToEtb(santim);
  const negative = plain.startsWith('-');
  const [whole = '0', fraction = '00'] = (negative ? plain.slice(1) : plain).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const number = `${negative ? '-' : ''}${grouped}.${fraction}`;
  if (options.bare) return number;
  const label = options.language === 'am' ? 'ብር' : 'ETB';
  return `${label} ${number}`;
}

/** 155 -> "1.55x". Input is odds multiplied by 100. */
export function formatOdds(oddsX100: number): string {
  if (!Number.isInteger(oddsX100) || oddsX100 < 0) {
    throw new SharedError('INVALID_MONEY', 'oddsX100 must be a non-negative integer');
  }
  const whole = Math.floor(oddsX100 / 100);
  const fraction = String(oddsX100 % 100).padStart(2, '0');
  return `${whole}.${fraction}x`;
}
